import { useTranslation } from 'react-i18next';

import { useDeletePlaylistContent, useDuplicatePlaylistContent } from '../../../queries';
import MediaThumb from './components/MediaThumb';
import PlaylistItemDuration from './components/PlaylistItemDuration';

// Prototype's dotted 6-dot grip icon (.claude/pp-playlist-page-prototype.html:536,
// the `grip` entry in its `IC` svg-path table) — verbatim viewBox/circles.
const GripIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
    <circle cx="9" cy="6" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="18" r="1" />
    <circle cx="15" cy="6" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="18" r="1" />
  </svg>
);

const SPECIAL_TYPES = ['HDMI', 'CHANNEL', 'RSS', 'RSS_CUSTOM'];
const TYPE_LABEL_KEY = {
  AUDIO: 'show.show_type_audio',
  VIDEO: 'show.show_type_video',
  IMAGE: 'show.show_type_image',
};

/**
 * Standalone-playlist-page content row — .claude/pp-playlist-page-
 * prototype.html's `.item`/`.thumb`/`.it-txt`/`.it-actions` (the same
 * `pp-cb-item*` classes the campaign builder's CampaignCard `builderRow`
 * already ported, see setup.css). Rendered by DragDrop.jsx when its new
 * `builderRow` prop is set AND the route is a playlist page — the legacy
 * PlaylistCard.jsx stays completely untouched for any other caller (none
 * currently pass `builderRow` for the playlist branch).
 *
 * Every row returned by `GET /playlist/{id}/{layer}/media` is real media —
 * `playlist_id` on each row is always the PARENT playlist's own id (the
 * table's only FK back to the owning playlist, see
 * database/migrations/2022_10_18_082444_create_playlist_media_table.php in
 * the backend repo), never a reference to another, nested playlist. A
 * previous round of this component had a `item.playlist_id != null` branch
 * that rendered a `NestedPlaylistRow` instead — since that condition is
 * true on every real row, every item rendered as a bogus "nested playlist"
 * showing the SAME parent playlist's own content again. Deleted along with
 * `NestedPlaylistRow.jsx`; nesting one standalone playlist inside another
 * isn't a reachable case with today's schema (it's a real, separate,
 * working feature only at the CAMPAIGN level — see
 * `PlaylistAccordionRow.jsx` / `campaign_playlist_media.playlist_id`).
 *
 * Grip — previously `ImprovedDragHandle` (the pre-redesign component that
 * bundles its OWN "index+1/totalItems" position pill alongside the "≡"
 * icon), rendered here ALONGSIDE this row's own `.pp-cb-ix` index span
 * below — on a touch device that showed BOTH a "1/4" pill AND a plain "1"
 * on every row, plus an oversized handle eating most of the row's width
 * (confirmed via real-device screenshot at 375/390px: name crushed to
 * "a-very-...", duration meta pushed off). Swapped for the prototype's own
 * small dotted grip (`.claude/pp-playlist-page-prototype.html`'s `IC.grip`
 * svg, `.grip` class) — same `.pp-cb-item-grip` wrapper class the campaign
 * builder's CampaignCard already uses (so the existing desktop/mobile
 * sizing rules apply unchanged), with `dragHandle` (the real
 * @hello-pangea/dnd `dragHandleProps`) spread directly onto it so reorder
 * drag-start still fires from the same element the old icon used.
 * `ImprovedDragHandle` itself is untouched and still used by CampaignCard's
 * own (non-playlist-page) builder rows and every legacy non-builder caller
 * — this change is scoped to this component only.
 */
function PlaylistBuilderRow({ item, layer, playlistId, index, dragHandle, onOpenSettings, onPreview }) {
  const { t } = useTranslation();
  const deletePlaylistContent = useDeletePlaylistContent();
  const duplicatePlaylistContent = useDuplicatePlaylistContent();

  const gripNode = dragHandle && (
    <div className="pp-cb-item-grip" {...dragHandle}>
      <GripIcon />
    </div>
  );

  const upType = String(item.type || '').toUpperCase();
  const isSpecial = SPECIAL_TYPES.includes(upType);

  const removeFromPlaylist = async () => {
    await deletePlaylistContent.mutateAsync({ playlist_media_id: item.playlist_media_id, playlist_id: playlistId });
  };

  const duplicate = async () => {
    await duplicatePlaylistContent.mutateAsync({ campaignMediaId: item.playlist_media_id });
  };

  const thumb = isSpecial ? (
    <span className="pp-cb-item-thumb special">{String(item.type).replace(/_/g, ' ')}</span>
  ) : (
    <div className="pp-cb-item-thumb"><MediaThumb item={item} onPreview={onPreview} /></div>
  );

  const typeLabel = TYPE_LABEL_KEY[upType] ? t(TYPE_LABEL_KEY[upType]) : (item.type || '');

  return (
    <div className="pp-cb-item">
      {gripNode}
      <span className="pp-cb-ix">{index + 1}</span>
      {thumb}
      <div className="pp-cb-item-txt">
        <span className="n">{item.file_name}</span>
        <span className="m">
          {typeLabel} ·{' '}
          <PlaylistItemDuration item={item} playlistId={playlistId} layer={layer} />
          {Number(item.is_muted) === 1 && ` · ${t('dragdrop.muted')}`}
        </span>
      </div>
      <div className="pp-cb-item-actions">
        <button type="button" className="pp-cb-chipbtn" title={t('dragdrop.duplicate')} onClick={duplicate} disabled={duplicatePlaylistContent.isLoading}>
          <i className="fa-regular fa-clone" />
        </button>
        <button type="button" className="pp-cb-chipbtn" title={t('dragdrop.setting')} onClick={() => onOpenSettings?.(item)}>
          <i className="pi pi-cog" />
        </button>
        <button type="button" className="pp-cb-chipbtn del" title={t('buttons.delete')} onClick={removeFromPlaylist} disabled={deletePlaylistContent.isLoading}>
          <i className="bx bx-trash" />
        </button>
      </div>
    </div>
  );
}

export default PlaylistBuilderRow;
